import { useState } from 'react';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { createSha256 } from '@ibk/shared-utils';
import { ApiErrors } from '../../enums';
import { usePostTransferPixBatchMutation } from '../../services/pixApi';
import { handlePixError } from '../../utils/pixErrorHandler';
const ERRORS_MESSAGES = {
    [ApiErrors.CAN_NOT_TRANSFER_PIX]: 'transaction-not-done-try-again-later',
    [ApiErrors.NO_FUNDS_AVAILABLE]: 'insufficient-balance-modal-title',
    [ApiErrors.ERROR_NO_FUNDS_AVAILABLE]: 'insufficient-balance-modal-title',
    [ApiErrors.PIN_FAILURE_TO_VALIDATE]: 'pin-failure-to-validate-error',
    [ApiErrors.TIMEOUT_ERROR]: 'pix-timeout-error',
    [ApiErrors.PIX_LIMIT_EXCEEDED]: 'pix-limit-exceeded',
    [ApiErrors.PAYMENT_ALREADY_EXISTS]: 'payment-in-processing',
};
export function usePostPixBatchTransfer() {
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed, cache } = useFeatureFlag(undefined, {
        autoLoad: false,
    });
    const [trigger] = usePostTransferPixBatchMutation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState();
    const [result, setResult] = useState();
    async function postPixBatchTransfer(body, credential, transactionAt, rethrow) {
        setLoading(true);
        setError(undefined);
        setResult(undefined);
        try {
            const usePermissions = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAYMENTS_OPERATOR_REQUEST_PAYMENT, undefined, cache?.data);
            const selectedAccountId = getSelectedAccountInfo()?.accountId;
            const transactionIdempotencyId = await createSha256({
                transactionAt,
                accountId: selectedAccountId,
                items: body.items,
                pin: credential,
            });
            const { result: response } = await trigger({
                body: { ...body, credential },
                transactionIdempotencyId,
                usePermissions,
            }).unwrap();
            setResult(response);
            return response;
        }
        catch (err) {
            const errorMessage = handlePixError({
                error: err,
                translate: t,
                errorMessages: ERRORS_MESSAGES,
                defaultErrorKey: ApiErrors.CAN_NOT_TRANSFER_PIX,
            });
            setError(errorMessage);
            if (rethrow) {
                throw new Error(errorMessage);
            }
        }
        finally {
            setLoading(false);
        }
    }
    return {
        loading,
        setLoading,
        error,
        setError,
        result,
        setResult,
        postPixBatchTransfer,
    };
}
